import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Alert,
  Box,
  Button,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useMutation } from "@apollo/client";
import { useState } from "react";
import { useAlerts } from "components/Alerts/AlertProvider";
import { RELEASE_CREDENTIAL_LOCK } from "components/Encampy/api";
import { EncampyPortalLoginLauncher } from "components/Encampy/PortalLoginLauncher";
import { CredentialFieldsDisplay } from "components/PortalCredential/CredentialFieldsDisplay";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { FieldDisplay } from "../Fulfillment/FulfillmentDetails/shared/FieldComponents";
import { useCohortDetails } from "./useCohortDetails";

export function CohortCredentials() {
  const theme = useTheme();
  const alerts = useAlerts();
  const { data } = useCohortDetails();
  const [activeLockId, setActiveLockId] = useState<string | null>(null);
  const [releaseCredentialLock] = useMutation(RELEASE_CREDENTIAL_LOCK);

  const [isExpanded, setIsExpanded] = useLocalStorageState<boolean>(
    "cohort-credentials-expanded",
    { defaultValue: true }
  );

  const cohort = data?.cohort;
  const cohortState = cohort?.state;
  const portalCredential = cohort?.portalCredential;
  const credential = portalCredential?.credential;
  const reportingYear = cohort?.reportingYear;
  const encampyDisabled = !credential?.username || !credential?.password;

  // Don't render if no portal credential data at all
  if (!portalCredential) {
    return (
      <Stack
        direction="column"
        component={Paper}
        elevation={0}
        spacing={2}
        padding={theme.spacing(2, 3)}
      >
        <Typography variant="h6">State Portal</Typography>
        <Typography variant="body2" color="text.secondary">
          No credential associated with cohort and no credentials found among
          members. Ensure at least one cohort member has a TIERII credential
          assigned to their facility.
        </Typography>
      </Stack>
    );
  }

  // Portal info (url, tech) returned but no matching login credential found among members
  if (!credential) {
    return (
      <Stack
        direction="column"
        component={Paper}
        elevation={0}
        spacing={2}
        padding={theme.spacing(2, 3)}
      >
        <Typography variant="h6">State Portal</Typography>
        {portalCredential.message ? (
          <Alert severity="warning">{portalCredential.message}</Alert>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No credential associated with cohort and no credentials found among
            members. Ensure at least one cohort member has a TIERII credential
            assigned to their facility.
          </Typography>
        )}
      </Stack>
    );
  }

  const credentialsContent = (
    <CredentialFieldsDisplay
      credential={credential}
      portalUrl={portalCredential.url}
      showCombinedInvoicing={cohortState === "WV"}
    />
  );

  const verificationStatusOnly = credential && (
    <FieldDisplay
      label="Verification Status:"
      value={prettyPrintEnumValue(credential.verificationStatus)}
      noWrap
    />
  );

  return (
    <Stack
      direction="column"
      component={Paper}
      elevation={0}
      spacing={2}
      padding={theme.spacing(2, 3)}
    >
      {/* Header with title, Encampy button, and expand/collapse */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
          }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Typography variant="h6" data-testid="cohort-credentials-title">
            State Portal
          </Typography>
          <IconButton size="small">
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        <Stack alignItems="end">
          <EncampyPortalLoginLauncher
            portalCredential={portalCredential}
            credentialId={credential?.id}
            reportingYear={reportingYear}
            onLockAcquired={(lockId) => setActiveLockId(lockId)}
            onSuccess={(message) => alerts.success(message)}
            onError={(error) => alerts.error(error)}
            size="small"
            disabled={encampyDisabled}
            disabledReason={
              encampyDisabled
                ? "Portal credentials are required for portal login"
                : undefined
            }
          />
          {activeLockId && (
            <Button
              size="small"
              color="warning"
              onClick={async () => {
                try {
                  const { data: releaseData } = await releaseCredentialLock({
                    variables: { lockId: activeLockId },
                  });
                  if (releaseData?.releaseCredentialLock) {
                    setActiveLockId(null);
                    alerts.success("Credential lock released");
                  } else {
                    alerts.error("Failed to release credential lock");
                  }
                } catch {
                  alerts.error("Failed to release credential lock");
                }
              }}
            >
              Release credential
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Show only verification status when collapsed */}
      {!isExpanded && verificationStatusOnly && (
        <Box>{verificationStatusOnly}</Box>
      )}

      <Collapse in={isExpanded}>
        <Stack spacing={2}>
          {/* Credentials content */}
          {credentialsContent}
        </Stack>
      </Collapse>
    </Stack>
  );
}
